import './App.css';
import { useState, useEffect, useCallback } from 'react';
import DashboardHeader from './dashboardheader.js'
import VerticalNavigation from './VerticalNavigation.js'
import bhcalc from './bhcalc.js'

const emptyForm = { programName: '', programType: '', startDate: '', endDate: '' };
const emptyTypeForm = { name: '', description: '' };

function Programs() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [programTypes, setProgramTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);

  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editingProgram, setEditingProgram] = useState(null);
  const [editFormData, setEditFormData] = useState(emptyForm);
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [editError, setEditError] = useState('');
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  const [deletingProgram, setDeletingProgram] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [showAddTypeModal, setShowAddTypeModal] = useState(false);
  const [typeFormData, setTypeFormData] = useState(emptyTypeForm);
  const [typeFormError, setTypeFormError] = useState('');
  const [isTypeSubmitting, setIsTypeSubmitting] = useState(false);

  const [editingType, setEditingType] = useState(null);
  const [editTypeFormData, setEditTypeFormData] = useState(emptyTypeForm);
  const [editTypeError, setEditTypeError] = useState('');
  const [isEditTypeSubmitting, setIsEditTypeSubmitting] = useState(false);

  const [deletingType, setDeletingType] = useState(null);
  const [isDeletingType, setIsDeletingType] = useState(false);
  const [deleteTypeError, setDeleteTypeError] = useState('');

  const fetchPrograms = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await bhcalc.get('/program');
      setPrograms(data);
    } catch (err) {
      console.error('Error fetching programs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProgramTypes = useCallback(async () => {
    try {
      setTypesLoading(true);
      const { data } = await bhcalc.get('/program-type');
      setProgramTypes(data);
    } catch (err) {
      console.error('Error fetching program types:', err);
    } finally {
      setTypesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrograms();
    fetchProgramTypes();
  }, [fetchPrograms, fetchProgramTypes]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImageFile(null);
      setImagePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('programName', formData.programName);
      fd.append('programType', formData.programType);
      if (formData.startDate) fd.append('startDate', formData.startDate);
      if (formData.endDate) fd.append('endDate', formData.endDate);
      if (imageFile) fd.append('image', imageFile);

      await bhcalc.post('/program', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setShowAddModal(false);
      setFormData(emptyForm);
      setImageFile(null);
      setImagePreview(null);
      fetchPrograms();
      fetchProgramTypes();
    } catch (err) {
      console.error('Error creating program:', err);
      if (err.response?.status === 400) setFormError('Invalid program data. Please check your inputs.');
      else if (err.response?.status === 401) setFormError('Session expired. Please log in again.');
      else if (err.response?.status === 403) setFormError('You do not have permission to create programs.');
      else if (err.response?.status === 500) setFormError('Server error. Please try again later.');
      else setFormError('Unable to create program. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowAddModal(false);
    setFormData(emptyForm);
    setImageFile(null);
    setImagePreview(null);
    setFormError('');
  };

  const openEdit = (program) => {
    setEditingProgram(program);
    setEditFormData({
      programName: program.programName || '',
      programType: program.programType?.name || '',
      startDate: program.startDate || '',
      endDate: program.endDate || '',
    });
    setEditImageFile(null);
    setEditImagePreview(null);
    setEditError('');
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setEditImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setEditImageFile(null);
      setEditImagePreview(null);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditError('');
    setIsEditSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('programName', editFormData.programName);
      fd.append('programType', editFormData.programType);
      if (editFormData.startDate) fd.append('startDate', editFormData.startDate);
      if (editFormData.endDate) fd.append('endDate', editFormData.endDate);
      if (editImageFile) fd.append('image', editImageFile);

      await bhcalc.put(`/program/${editingProgram.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setEditingProgram(null);
      fetchPrograms();
      fetchProgramTypes();
    } catch (err) {
      console.error('Error updating program:', err);
      if (err.response?.status === 400) setEditError('Invalid program data. Please check your inputs.');
      else if (err.response?.status === 401) setEditError('Session expired. Please log in again.');
      else if (err.response?.status === 403) setEditError('You do not have permission to edit programs.');
      else if (err.response?.status === 404) setEditError('Program not found.');
      else if (err.response?.status === 500) setEditError('Server error. Please try again later.');
      else setEditError('Unable to update program. Please try again.');
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleEditCancel = () => {
    setEditingProgram(null);
    setEditError('');
  };

  const openDelete = (program) => {
    setDeletingProgram(program);
    setDeleteError('');
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    setDeleteError('');
    try {
      await bhcalc.delete(`/program/${deletingProgram.id}`);
      setDeletingProgram(null);
      fetchPrograms();
    } catch (err) {
      console.error('Error deleting program:', err);
      if (err.response?.status === 401) setDeleteError('Session expired. Please log in again.');
      else if (err.response?.status === 403) setDeleteError('You do not have permission to delete programs.');
      else if (err.response?.status === 404) setDeleteError('Program not found.');
      else if (err.response?.status === 500) setDeleteError('Server error. Please try again later.');
      else setDeleteError('Unable to delete program. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTypeSubmit = async (e) => {
    e.preventDefault();
    setTypeFormError('');
    setIsTypeSubmitting(true);
    try {
      await bhcalc.post('/program-type', typeFormData);
      setShowAddTypeModal(false);
      setTypeFormData(emptyTypeForm);
      fetchProgramTypes();
    } catch (err) {
      console.error('Error creating program type:', err);
      if (err.response?.status === 400) setTypeFormError('Invalid data. Please check your inputs.');
      else if (err.response?.status === 401) setTypeFormError('Session expired. Please log in again.');
      else if (err.response?.status === 403) setTypeFormError('You do not have permission to create program types.');
      else if (err.response?.status === 409) setTypeFormError('A program type with that name already exists.');
      else if (err.response?.status === 500) setTypeFormError('Server error. Please try again later.');
      else setTypeFormError('Unable to create program type. Please try again.');
    } finally {
      setIsTypeSubmitting(false);
    }
  };

  const handleTypeCancel = () => {
    setShowAddTypeModal(false);
    setTypeFormData(emptyTypeForm);
    setTypeFormError('');
  };

  const openEditType = (type) => {
    setEditingType(type);
    setEditTypeFormData({ name: type.name || '', description: type.description || '' });
    setEditTypeError('');
  };

  const handleEditTypeSubmit = async (e) => {
    e.preventDefault();
    setEditTypeError('');
    setIsEditTypeSubmitting(true);
    try {
      await bhcalc.put(`/program-type/${editingType.id}`, editTypeFormData);
      setEditingType(null);
      fetchProgramTypes();
    } catch (err) {
      console.error('Error updating program type:', err);
      if (err.response?.status === 400) setEditTypeError('Invalid data. Please check your inputs.');
      else if (err.response?.status === 401) setEditTypeError('Session expired. Please log in again.');
      else if (err.response?.status === 403) setEditTypeError('You do not have permission to edit program types.');
      else if (err.response?.status === 404) setEditTypeError('Program type not found.');
      else if (err.response?.status === 409) setEditTypeError('A program type with that name already exists.');
      else if (err.response?.status === 500) setEditTypeError('Server error. Please try again later.');
      else setEditTypeError('Unable to update program type. Please try again.');
    } finally {
      setIsEditTypeSubmitting(false);
    }
  };

  const handleDeleteTypeConfirm = async () => {
    setIsDeletingType(true);
    setDeleteTypeError('');
    try {
      await bhcalc.delete(`/program-type/${deletingType.id}`);
      setDeletingType(null);
      fetchProgramTypes();
    } catch (err) {
      console.error('Error deleting program type:', err);
      if (err.response?.status === 401) setDeleteTypeError('Session expired. Please log in again.');
      else if (err.response?.status === 403) setDeleteTypeError('You do not have permission to delete program types.');
      else if (err.response?.status === 404) setDeleteTypeError('Program type not found.');
      else if (err.response?.status === 500) setDeleteTypeError('Server error. Please try again later.');
      else setDeleteTypeError('Unable to delete program type. Please try again.');
    } finally {
      setIsDeletingType(false);
    }
  };

  return (
    <div className="App min-h-screen">
      <div className="dashboard-wrapper flex flex-col min-h-screen">
        <div className="dashboard-header flex w-full max-h-24 h-14">
          <DashboardHeader />
        </div>
        <div className="w-full flex flex-wrap min-h-screen">
          <div className="dashboard-left-sidebar flex-none w-48 relative">
            <VerticalNavigation />
          </div>
          <div className="dashboard-right-main border-1 flex-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-left">Programs</h2>
              <button
                onClick={() => { fetchProgramTypes(); setShowAddModal(true); }}
                className="px-3 py-1 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
              >
                <span className="text-xl">+</span>
                <span>Add Program</span>
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Program Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Start Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">End Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Image</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-left">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">Loading programs...</td>
                    </tr>
                  ) : programs.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">No programs found. Add your first program above.</td>
                    </tr>
                  ) : (
                    programs.map((program) => (
                      <tr key={program.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{program.programName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{program.programType?.name || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{program.startDate || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{program.endDate || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {program.imageUrl
                            ? <img src={program.imageUrl} alt={program.programName} className="h-8 w-8 object-cover rounded" />
                            : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => openEdit(program)}
                            className="text-indigo-600 hover:text-indigo-900 mr-3"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => openDelete(program)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {programs.length > 0 && (
              <div className="mt-4">
                <span className="text-sm text-gray-600">Showing {programs.length} program{programs.length !== 1 ? 's' : ''}</span>
              </div>
            )}

            <div className="flex justify-between items-center mb-6 mt-10">
              <h2 className="text-2xl font-bold text-left">Program Types</h2>
              <button
                onClick={() => setShowAddTypeModal(true)}
                className="px-3 py-1 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
              >
                <span className="text-xl">+</span>
                <span>Add Program Type</span>
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-left">
                  {typesLoading ? (
                    <tr>
                      <td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">Loading program types...</td>
                    </tr>
                  ) : programTypes.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">No program types found. Add your first program type above.</td>
                    </tr>
                  ) : (
                    programTypes.map((type) => (
                      <tr key={type.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{type.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{type.description || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => openEditType(type)}
                            className="text-indigo-600 hover:text-indigo-900 mr-3"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => { setDeletingType(type); setDeleteTypeError(''); }}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {programTypes.length > 0 && (
              <div className="mt-4">
                <span className="text-sm text-gray-600">Showing {programTypes.length} program type{programTypes.length !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed text-left inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Add New Program</h2>
                <button
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                {formError && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{formError}</div>
                )}
                <ProgramFormFields
                  data={formData}
                  onChange={handleChange}
                  onImageChange={handleImageChange}
                  imagePreview={imagePreview}
                  programTypes={programTypes}
                />
                <div className="flex justify-end space-x-3 pt-4 border-t mt-6">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Adding...' : 'Add Program'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingProgram && (
        <div className="fixed text-left inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Program</h2>
                <button
                  onClick={handleEditCancel}
                  disabled={isEditSubmitting}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleEditSubmit}>
                {editError && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{editError}</div>
                )}
                <ProgramFormFields
                  data={editFormData}
                  onChange={handleEditChange}
                  onImageChange={handleEditImageChange}
                  imagePreview={editImagePreview}
                  programTypes={programTypes}
                  existingImageUrl={editingProgram.imageUrl}
                />
                <div className="flex justify-end space-x-3 pt-4 border-t mt-6">
                  <button
                    type="button"
                    onClick={handleEditCancel}
                    disabled={isEditSubmitting}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isEditSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isEditSubmitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingProgram && (
        <div className="fixed text-left inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Delete Program</h2>
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete <span className="font-semibold">{deletingProgram.programName}</span>? This action cannot be undone.
              </p>
              {deleteError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{deleteError}</div>
              )}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeletingProgram(null)}
                  disabled={isDeleting}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Program Type Modal */}
      {showAddTypeModal && (
        <div className="fixed text-left inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Add New Program Type</h2>
                <button
                  onClick={handleTypeCancel}
                  disabled={isTypeSubmitting}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleTypeSubmit}>
                {typeFormError && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{typeFormError}</div>
                )}
                <ProgramTypeFormFields data={typeFormData} onChange={(e) => setTypeFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))} />
                <div className="flex justify-end space-x-3 pt-4 border-t mt-6">
                  <button
                    type="button"
                    onClick={handleTypeCancel}
                    disabled={isTypeSubmitting}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isTypeSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isTypeSubmitting ? 'Adding...' : 'Add Program Type'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Program Type Modal */}
      {editingType && (
        <div className="fixed text-left inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Program Type</h2>
                <button
                  onClick={() => { setEditingType(null); setEditTypeError(''); }}
                  disabled={isEditTypeSubmitting}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleEditTypeSubmit}>
                {editTypeError && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{editTypeError}</div>
                )}
                <ProgramTypeFormFields data={editTypeFormData} onChange={(e) => setEditTypeFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))} />
                <div className="flex justify-end space-x-3 pt-4 border-t mt-6">
                  <button
                    type="button"
                    onClick={() => { setEditingType(null); setEditTypeError(''); }}
                    disabled={isEditTypeSubmitting}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isEditTypeSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isEditTypeSubmitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Program Type Confirmation Modal */}
      {deletingType && (
        <div className="fixed text-left inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Delete Program Type</h2>
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete <span className="font-semibold">{deletingType.name}</span>? This action cannot be undone.
              </p>
              {deleteTypeError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{deleteTypeError}</div>
              )}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeletingType(null)}
                  disabled={isDeletingType}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteTypeConfirm}
                  disabled={isDeletingType}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeletingType ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProgramFormFields({ data, onChange, onImageChange, imagePreview, programTypes, existingImageUrl }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Program Name *</label>
        <input
          type="text"
          name="programName"
          value={data.programName}
          onChange={onChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter program name"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Program Type *</label>
        <select
          name="programType"
          value={data.programType}
          onChange={onChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
        >
          <option value="">Select a program type</option>
          {programTypes.map((type) => (
            <option key={type.id} value={type.name}>{type.name}</option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
          <input
            type="date"
            name="startDate"
            value={data.startDate}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
          <input
            type="date"
            name="endDate"
            value={data.endDate}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Program Image</label>
        {existingImageUrl && !imagePreview && (
          <div className="mb-2">
            <img src={existingImageUrl} alt="Current program" className="max-h-32 rounded-md border border-gray-200" />
            <p className="mt-1 text-xs text-gray-500">Upload a new image to replace the current one</p>
          </div>
        )}
        <input
          type="file"
          accept="image/*"
          onChange={onImageChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        />
        {imagePreview && (
          <div className="mt-2">
            <img src={imagePreview} alt="Program preview" className="max-h-32 rounded-md border border-gray-200" />
          </div>
        )}
      </div>
    </div>
  );
}

function ProgramTypeFormFields({ data, onChange }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
        <input
          type="text"
          name="name"
          value={data.name}
          onChange={onChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter program type name"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          name="description"
          value={data.description}
          onChange={onChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Enter a description (optional)"
        />
      </div>
    </div>
  );
}

export default Programs;
